import { useCallback, useEffect, useState } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { PremiumBadge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { MicroCircleContainer } from '../../../../components/CircleContainer/styles';
import Header from '../../../../components/Header';
import { LoadingModal } from '../../../../components/LoadingModal';
import constants from '../../../../constants';
import { useQueryUrl } from '../../../../hooks/QueryUrl';
import CheckIcon from '../../../../icons/Check';
import { keepoInternalAnalyticsService } from '../../../../services/analytics';
import { api, ApiSiteResponse } from '../../../../services/api';
import { ItemSelection } from '../../components/ItemSelection';
import { PaymentInfo } from '../../components/PaymentInfo';
import { CREATE_NEW_KEEPO } from '../SiteSelection';
import { Container } from './styles';

type OfferItemProps = {
    title: string;
};

const OfferItem = memo(function OfferItem({ title }: OfferItemProps) {
    return (
        <div className="item">
            <MicroCircleContainer $color="var(--color-neutral-dark)">
                <CheckIcon color="var(--color-lemon)" height={8} width={10} />
            </MicroCircleContainer>
            <h6>{title}</h6>
        </div>
    );
});

const items = [
    'analytics',
    'analyticsIntegration',
    'form',
    'media',
    'style',
    'integrations',
];

export const FeaturesPage = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const queryUrl = useQueryUrl();

    const [loading, setLoading] = useState(true);
    const [sites, setSites] = useState<ApiSiteResponse[]>([]);

    const [planType, setPlanType] = useState<'MONTHLY' | 'ANNUALLY'>(
        'ANNUALLY'
    );

    const callCheckoutHandler = useCallback(
        async (siteId?: string) => {
            const url = await api.redirectCheckout({
                plan: planType,
                siteId,
                currentPage: window.location.href,
            });

            const user = await api.getUser();
            keepoInternalAnalyticsService.notifyCheckoutEvent(user);

            window.location = url;
        },
        [planType]
    );

    const continueHandler = async () => {
        setLoading(true);
        const selectedSite = queryUrl.get('site');

        if (!sites.length) {
            return await callCheckoutHandler();
        }

        if (!!selectedSite && selectedSite !== CREATE_NEW_KEEPO) {
            return await callCheckoutHandler(selectedSite);
        }

        history.push(`/subscription/selection?plan=${planType}`);
    };

    const loadSitesHandler = useCallback(async () => {
        const sites = (await api.getSites()).filter(
            (site) => site.subscription?.type !== 'PLUS' && !!site.site
        );
        setSites(sites);
        setLoading(false);
    }, []);

    const notifyPreCheckout = async () => {
        const user = await api.getUser();
        keepoInternalAnalyticsService.notifyPreCheckoutEvent(user);
    };

    useEffect(() => {
        loadSitesHandler();
    }, [loadSitesHandler]);

    useEffect(() => {
        notifyPreCheckout();
    }, []);

    return loading ? (
        <LoadingModal />
    ) : (
        <Container>
            <Header title={t('pages.subscription.header.title')} />
            <div className="group-image">
                <PremiumBadge className="special-price-banner">
                    {t('pages.subscription.features.offer.specialPrice')}
                </PremiumBadge>
            </div>
            <div className="group-prices">
                <div className="prices-selection">
                    <ItemSelection
                        tabIndex={1}
                        title={t(
                            'pages.subscription.features.plans.monthly.title'
                        )}
                        active={planType === 'MONTHLY'}
                        onClick={() => setPlanType('MONTHLY')}
                        rightChildren={
                            <div className="price-detail">
                                <p>{constants.price.month.value}</p>
                            </div>
                        }
                    />
                    <ItemSelection
                        tabIndex={2}
                        title={t(
                            'pages.subscription.features.plans.annually.title'
                        )}
                        active={planType === 'ANNUALLY'}
                        onClick={() => setPlanType('ANNUALLY')}
                        rightChildren={
                            <div className="price-detail">
                                <p className="old-price line-through">
                                    {constants.price.year.oldValue}
                                </p>
                                <p>{constants.price.year.value}</p>
                            </div>
                        }
                    />
                </div>

                <div className="prices-info">
                    <p>
                        {t('pages.subscription.features.pricesInfo').replace(
                            '${value}',
                            constants.price.year.oldValue
                        )}
                    </p>
                </div>
            </div>
            <div className="offer-items-wrapper">
                <div className="offer-items">
                    {items.map((item) => (
                        <OfferItem
                            key={item}
                            title={t(
                                `pages.subscription.features.offer.items.${item}`
                            )}
                        />
                    ))}
                </div>
            </div>
            <PaymentInfo />
            <div className="group-button">
                <div className="fixed-button">
                    <Button
                        type="WHITE"
                        size="LG"
                        onClick={continueHandler}
                        tabIndex={3}
                    >
                        {t('pages.subscription.continueButton')}
                    </Button>
                </div>
            </div>
        </Container>
    );
};
